/**
 * Listens to AuthContext + route + company membership changes and pushes
 * super properties (account_type, surface, company_id, plan_tier...) to the
 * analytics bus so every event carries the same context.
 *
 * Also detects subscription state transitions to emit `subscription_activated`
 * and `subscription_canceled` from the client (server-side will dedupe via
 * the `analytics-emit` edge function once available).
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useOptionalAuth } from '@/contexts/AuthContext';
import { useMyCompanies } from '@/hooks/useMyCompanies';
import { setSuperProperties, detectSurface, resolveAccountType } from '@/lib/analytics';

const COMPANY_ROLE_MAP: Record<string, 'manager' | 'coordinator' | 'caregiver'> = {
  manager: 'manager',
  coordinator: 'coordinator',
  employee: 'caregiver',
};

export default function AnalyticsContextBridge() {
  const auth = useOptionalAuth();
  const user = auth?.user ?? null;
  const roles = auth?.roles ?? [];
  const subscription = auth?.subscription;
  const hasCheckedSubscription = auth?.hasCheckedSubscription ?? false;
  const { companies } = useMyCompanies(user?.id ?? null);
  const location = useLocation();

  // Account type / company / role super properties
  useEffect(() => {
    const company = companies[0];
    const isOwner = roles.includes('owner');
    const isCaregiver = roles.includes('caregiver');
    const accountType = resolveAccountType({
      hasUser: !!user,
      isCompanyMember: !!company,
      companyRole: company ? COMPANY_ROLE_MAP[company.role] : null,
      isOwner,
      isCaregiver,
    });

    setSuperProperties({
      account_type: accountType,
      company_id: company?.companyId ?? null,
      is_company_plan: subscription?.isCompanyPlan ?? false,
      plan_tier: subscription?.productId ?? null,
      subscription_status: hasCheckedSubscription
        ? subscription?.subscribed ? 'active' : 'inactive'
        : 'unknown',
    });
  }, [user, roles, companies, subscription, hasCheckedSubscription]);

  // Surface tracking (landing/app/caregiver_app/company_console)
  useEffect(() => {
    setSuperProperties({ surface: detectSurface(location.pathname) });
  }, [location.pathname]);

  // Subscription state transitions are emitted SERVER-SIDE only
  // (check-subscription / stripe-webhook / sync-stripe-subscriptions), where
  // `analytics-emit` dedupes via milestones. Emitting them here too caused
  // double counting of `subscription_activated` / `subscription_canceled`,
  // and they were lost entirely when the user declined analytics cookies.


  return null;
}
